export interface InvoiceItem {
  id: string;
  description: string;
  additionalDetails?: string;
  quantity: number;
  rate: number;
  amount: number;
}

export interface Invoice {
  id: string;
  invoiceNumber: string;
  date: string;
  dueDate: string;
  terms?: string;
  orderNumber?: string;
  company: {
    name: string;
    businessName?: string;
    email: string;
    address: string;
    phone: string;
    website: string;
    businessNumber?: string;
    additionalDetails?: boolean; // Changed to boolean to control showing additional details
    mobile?: string;
    fax?: string;
  };
  billTo: {
    name: string;
    email: string;
    address: string;
    phone?: string;
    mobile?: string;
    fax?: string;
  };
  items: InvoiceItem[];
  subtotal: number;
  tax: number;
  taxRate: number;
  total: number;
  amountPaid?: number;
  balanceDue?: number;
  currency: string;
  notes: string;
  termsAndConditions?: string;
  signature?: string;
  photos?: string[];
  createdAt: string;
  updatedAt: string;
}

export interface User {
  id: string;
  email: string;
  name: string;
  provider: 'email' | 'google';
  createdAt: string;
}

export interface TemplateAccess {
  id: string;
  user_id: string;
  template_id: string;
  purchased_at: string;
  expires_at: string;
  access_type: 'single_template';
  created_at: string;
  updated_at: string;
}

export interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  publishedAt: string;
  readTime: number;
}

export const CURRENCIES = [
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'BRL', symbol: 'R$', name: 'Brazilian Real' },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
  { code: 'AED', symbol: 'د.إ', name: 'United Arab Emirates Dirham' },
  { code: 'AFN', symbol: '؋', name: 'Afghan Afghani' },
  { code: 'ALL', symbol: 'L', name: 'Albanian Lek' },
  { code: 'AMD', symbol: '֏', name: 'Armenian Dram' },
  { code: 'ANG', symbol: 'ƒ', name: 'Netherlands Antillean Guilder' },
  { code: 'AOA', symbol: 'Kz', name: 'Angolan Kwanza' },
  { code: 'ARS', symbol: '$', name: 'Argentine Peso' },
  { code: 'AWG', symbol: 'ƒ', name: 'Aruban Florin' },
  { code: 'AZN', symbol: '₼', name: 'Azerbaijani Manat' },
  { code: 'BAM', symbol: 'KM', name: 'Bosnia-Herzegovina Convertible Mark' },
  { code: 'BBD', symbol: '$', name: 'Barbadian Dollar' },
  { code: 'BDT', symbol: '৳', name: 'Bangladeshi Taka' },
  { code: 'BGN', symbol: 'лв', name: 'Bulgarian Lev' },
  { code: 'BHD', symbol: '.د.ب', name: 'Bahraini Dinar' },
  { code: 'BIF', symbol: 'FBu', name: 'Burundian Franc' },
  { code: 'BMD', symbol: '$', name: 'Bermudan Dollar' },
  { code: 'BND', symbol: '$', name: 'Brunei Dollar' },
  { code: 'BOB', symbol: 'Bs.', name: 'Bolivian Boliviano' },
  { code: 'BSD', symbol: '$', name: 'Bahamian Dollar' },
  { code: 'BTN', symbol: 'Nu.', name: 'Bhutanese Ngultrum' },
  { code: 'BWP', symbol: 'P', name: 'Botswanan Pula' },
  { code: 'BYN', symbol: 'Br', name: 'Belarusian Ruble' },
  { code: 'BZD', symbol: 'BZ$', name: 'Belize Dollar' },
  { code: 'CHF', symbol: 'CHF', name: 'Swiss Franc' },
  { code: 'CLP', symbol: '$', name: 'Chilean Peso' },
  { code: 'CNY', symbol: '¥', name: 'Chinese Yuan' },
  { code: 'COP', symbol: '$', name: 'Colombian Peso' },
  { code: 'CRC', symbol: '₡', name: 'Costa Rican Colón' },
  { code: 'CVE', symbol: '$', name: 'Cape Verdean Escudo' },
  { code: 'CZK', symbol: 'Kč', name: 'Czech Koruna' },
  { code: 'DJF', symbol: 'Fdj', name: 'Djiboutian Franc' },
  { code: 'DKK', symbol: 'kr', name: 'Danish Krone' },
  { code: 'DOP', symbol: 'RD$', name: 'Dominican Peso' },
  { code: 'DZD', symbol: 'د.ج', name: 'Algerian Dinar' },
  { code: 'EGP', symbol: 'ج.م', name: 'Egyptian Pound' },
  { code: 'ETB', symbol: 'Br', name: 'Ethiopian Birr' },
  { code: 'FJD', symbol: '$', name: 'Fijian Dollar' },
  { code: 'GEL', symbol: '₾', name: 'Georgian Lari' },
  { code: 'GHS', symbol: '₵', name: 'Ghanaian Cedi' },
  { code: 'GMD', symbol: 'D', name: 'Gambian Dalasi' },
  { code: 'GTQ', symbol: 'Q', name: 'Guatemalan Quetzal' },
  { code: 'GYD', symbol: '$', name: 'Guyanaese Dollar' },
  { code: 'HKD', symbol: '$', name: 'Hong Kong Dollar' },
  { code: 'HNL', symbol: 'L', name: 'Honduran Lempira' },
  { code: 'HRK', symbol: 'kn', name: 'Croatian Kuna' },
  { code: 'HTG', symbol: 'G', name: 'Haitian Gourde' },
  { code: 'HUF', symbol: 'Ft', name: 'Hungarian Forint' },
  { code: 'IDR', symbol: 'Rp', name: 'Indonesian Rupiah' },
  { code: 'ILS', symbol: '₪', name: 'Israeli New Shekel' },
  { code: 'ISK', symbol: 'kr', name: 'Icelandic Króna' },
  { code: 'JMD', symbol: 'J$', name: 'Jamaican Dollar' },
  { code: 'JOD', symbol: 'د.ا', name: 'Jordanian Dinar' },
  { code: 'KES', symbol: 'KSh', name: 'Kenyan Shilling' },
  { code: 'KGS', symbol: 'сом', name: 'Kyrgystani Som' },
  { code: 'KHR', symbol: '៛', name: 'Cambodian Riel' },
  { code: 'KMF', symbol: 'CF', name: 'Comorian Franc' },
  { code: 'KRW', symbol: '₩', name: 'South Korean Won' },
  { code: 'KWD', symbol: 'د.ك', name: 'Kuwaiti Dinar' },
  { code: 'KYD', symbol: '$', name: 'Cayman Islands Dollar' },
  { code: 'KZT', symbol: '₸', name: 'Kazakhstani Tenge' },
  { code: 'LAK', symbol: '₭', name: 'Laotian Kip' },
  { code: 'LBP', symbol: 'ل.ل', name: 'Lebanese Pound' },
  { code: 'LKR', symbol: '₨', name: 'Sri Lankan Rupee' },
  { code: 'LRD', symbol: '$', name: 'Liberian Dollar' },
  { code: 'LSL', symbol: 'M', name: 'Lesotho Loti' },
  { code: 'MAD', symbol: 'د.م.', name: 'Moroccan Dirham' },
  { code: 'MDL', symbol: 'L', name: 'Moldovan Leu' },
  { code: 'MGA', symbol: 'Ar', name: 'Malagasy Ariary' },
  { code: 'MKD', symbol: 'ден', name: 'Macedonian Denar' },
  { code: 'MMK', symbol: 'K', name: 'Myanmar Kyat' },
  { code: 'MNT', symbol: '₮', name: 'Mongolian Tugrik' },
  { code: 'MOP', symbol: 'MOP$', name: 'Macanese Pataca' },
  { code: 'MUR', symbol: '₨', name: 'Mauritian Rupee' },
  { code: 'MVR', symbol: '.ރ', name: 'Maldivian Rufiyaa' },
  { code: 'MWK', symbol: 'MK', name: 'Malawian Kwacha' },
  { code: 'MXN', symbol: '$', name: 'Mexican Peso' },
  { code: 'MYR', symbol: 'RM', name: 'Malaysian Ringgit' },
  { code: 'MZN', symbol: 'MT', name: 'Mozambican Metical' },
  { code: 'NAD', symbol: '$', name: 'Namibian Dollar' },
  { code: 'NGN', symbol: '₦', name: 'Nigerian Naira' },
  { code: 'NIO', symbol: 'C$', name: 'Nicaraguan Córdoba' },
  { code: 'NOK', symbol: 'kr', name: 'Norwegian Krone' },
  { code: 'NPR', symbol: '₨', name: 'Nepalese Rupee' },
  { code: 'NZD', symbol: '$', name: 'New Zealand Dollar' },
  { code: 'OMR', symbol: 'ر.ع.', name: 'Omani Rial' },
  { code: 'PAB', symbol: 'B/.', name: 'Panamanian Balboa' },
  { code: 'PEN', symbol: 'S/.', name: 'Peruvian Nuevo Sol' },
  { code: 'PGK', symbol: 'K', name: 'Papua New Guinean Kina' },
  { code: 'PHP', symbol: '₱', name: 'Philippine Peso' },
  { code: 'PKR', symbol: '₨', name: 'Pakistani Rupee' },
  { code: 'PLN', symbol: 'zł', name: 'Polish Złoty' },
  { code: 'PYG', symbol: '₲', name: 'Paraguayan Guarani' },
  { code: 'QAR', symbol: 'ر.ق', name: 'Qatari Rial' },
  { code: 'RON', symbol: 'lei', name: 'Romanian Leu' },
  { code: 'RSD', symbol: 'дин.', name: 'Serbian Dinar' },
  { code: 'RUB', symbol: '₽', name: 'Russian Ruble' },
  { code: 'RWF', symbol: 'FRw', name: 'Rwandan Franc' },
  { code: 'SAR', symbol: 'ر.س', name: 'Saudi Riyal' },
  { code: 'SBD', symbol: '$', name: 'Solomon Islands Dollar' },
  { code: 'SCR', symbol: '₨', name: 'Seychellois Rupee' },
  { code: 'SEK', symbol: 'kr', name: 'Swedish Krona' },
  { code: 'SGD', symbol: '$', name: 'Singapore Dollar' },
  { code: 'SHP', symbol: '£', name: 'Saint Helena Pound' },
  { code: 'SLE', symbol: 'Le', name: 'Sierra Leonean Leone' },
  { code: 'SOS', symbol: 'S', name: 'Somali Shilling' },
  { code: 'SRD', symbol: '$', name: 'Surinamese Dollar' },
  { code: 'STD', symbol: 'Db', name: 'São Tomé and Príncipe Dobra' },
  { code: 'SVC', symbol: '$', name: 'Salvadoran Colón' },
  { code: 'SZL', symbol: 'E', name: 'Swazi Lilangeni' },
  { code: 'THB', symbol: '฿', name: 'Thai Baht' },
  { code: 'TJS', symbol: 'ЅМ', name: 'Tajikistani Somoni' },
  { code: 'TND', symbol: 'د.ت', name: 'Tunisian Dinar' },
  { code: 'TOP', symbol: 'T$', name: 'Tongan Paʻanga' },
  { code: 'TRY', symbol: '₺', name: 'Turkish Lira' },
  { code: 'TTD', symbol: 'TT$', name: 'Trinidad and Tobago Dollar' },
  { code: 'TWD', symbol: 'NT$', name: 'New Taiwan Dollar' },
  { code: 'TZS', symbol: 'TSh', name: 'Tanzanian Shilling' },
  { code: 'UAH', symbol: '₴', name: 'Ukrainian Hryvnia' },
  { code: 'UGX', symbol: 'USh', name: 'Ugandan Shilling' },
  { code: 'UYU', symbol: '$U', name: 'Uruguayan Peso' },
  { code: 'UZS', symbol: 'лв', name: 'Uzbekistan Som' },
  { code: 'VEF', symbol: 'Bs', name: 'Venezuelan Bolívar' },
  { code: 'VND', symbol: '₫', name: 'Vietnamese Dong' },
  { code: 'VUV', symbol: 'VT', name: 'Vanuatu Vatu' },
  { code: 'WST', symbol: 'WS$', name: 'Samoan Tala' },
  { code: 'XAF', symbol: 'FCFA', name: 'CFA Franc BEAC' },
  { code: 'XCD', symbol: '$', name: 'East Caribbean Dollar' },
  { code: 'XOF', symbol: 'CFA', name: 'CFA Franc BCEAO' },
  { code: 'XPF', symbol: '₣', name: 'CFP Franc' },
  { code: 'YER', symbol: '﷼', name: 'Yemeni Rial' },
  { code: 'ZAR', symbol: 'R', name: 'South African Rand' },
  { code: 'ZMW', symbol: 'ZK', name: 'Zambian Kwacha' },
] as const;

export interface InvoiceTemplate {
  id: string;
  name: string;
  description: string;
  price: number;
  isPremium: boolean;
  colors: {
    primary: string;
    secondary: string;
    accent: string;
    text: string;
    background: string;
  };
  logo?: string;
  preview: string;
  priceId?: string;
  category: 'business' | 'creative' | 'minimal' | 'corporate' | 'modern';
  features?: string[]; // Array de funcionalidades extras
  watermarkRemoval?: boolean; // Remoção de marca d'água
  customHeader?: boolean; // Cabeçalho personalizado
  customFooter?: boolean; // Rodapé personalizado
  attachmentSupport?: boolean; // Suporte para anexos
  multiCurrency?: boolean; // Suporte para múltiplas moedas
  digitalSignature?: boolean; // Assinatura digital
  itemImages?: boolean; // Imagens dos itens
}

export const INVOICE_TEMPLATES: InvoiceTemplate[] = [
  {
    id: 'classic',
    name: 'Classic Blue',
    description: 'Professional blue template perfect for any business',
    price: 0,
    isPremium: false,
    colors: {
      primary: '#2563EB',
      secondary: '#1E40AF',
      accent: '#3B82F6',
      text: '#374151',
      background: '#FFFFFF'
    },
    preview: 'https://images.pexels.com/photos/6863183/pexels-photo-6863183.jpeg?auto=compress&cs=tinysrgb&w=400',
    category: 'business',
    features: ['Formato básico', 'Campos padrão']
  },
  {
    id: 'emerald-pro',
    name: 'Emerald Professional',
    description: 'Elegant emerald green design with modern typography',
    price: 1.00,
    isPremium: true,
    colors: {
      primary: '#059669',
      secondary: '#047857',
      accent: '#10B981',
      text: '#374151',
      background: '#F0FDF4'
    },
    logo: 'https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=100',
    preview: 'https://images.pexels.com/photos/6863183/pexels-photo-6863183.jpeg?auto=compress&cs=tinysrgb&w=400',
    category: 'business',
    features: ['Tipografia moderna', 'Logo personalizado', 'Campos personalizáveis'],
    watermarkRemoval: true,
    priceId: 'price_1Rk95pBICvnzUZeSUfbjhprN'
  },
  {
    id: 'sunset-creative',
    name: 'Sunset Creative',
    description: 'Vibrant orange gradient perfect for creative agencies',
    price: 1.00,
    isPremium: true,
    colors: {
      primary: '#EA580C',
      secondary: '#DC2626',
      accent: '#F97316',
      text: '#374151',
      background: '#FFF7ED'
    },
    logo: 'https://images.pexels.com/photos/3184338/pexels-photo-3184338.jpeg?auto=compress&cs=tinysrgb&w=100',
    preview: 'https://images.pexels.com/photos/6863183/pexels-photo-6863183.jpeg?auto=compress&cs=tinysrgb&w=400',
    category: 'creative',
    features: ['Gradientes vibrantes', 'Campos para portfólio', 'Detalhes de projeto'],
    watermarkRemoval: true,
    customHeader: true,
    priceId: 'price_1Rk95pBICvnzUZeSUfbjhprN'
  },
  {
    id: 'purple-luxury',
    name: 'Purple Luxury',
    description: 'Sophisticated purple design for premium services',
    price: 1.00,
    isPremium: true,
    colors: {
      primary: '#7C3AED',
      secondary: '#6D28D9',
      accent: '#8B5CF6',
      text: '#374151',
      background: '#FAF5FF'
    },
    logo: 'https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg?auto=compress&cs=tinysrgb&w=100',
    preview: 'https://images.pexels.com/photos/6863183/pexels-photo-6863183.jpeg?auto=compress&cs=tinysrgb&w=400',
    category: 'corporate',
    features: ['Design luxuoso', 'Seção de serviços premium', 'Termos personalizados'],
    watermarkRemoval: true,
    customHeader: true,
    customFooter: true,
    priceId: 'price_1Rk95pBICvnzUZeSUfbjhprN'
  },
  {
    id: 'rose-elegant',
    name: 'Rose Elegant',
    description: 'Elegant rose gold design for beauty and wellness',
    price: 1.00,
    isPremium: true,
    colors: {
      primary: '#E11D48',
      secondary: '#BE185D',
      accent: '#F43F5E',
      text: '#374151',
      background: '#FFF1F2'
    },
    logo: 'https://images.pexels.com/photos/3184339/pexels-photo-3184339.jpeg?auto=compress&cs=tinysrgb&w=100',
    preview: 'https://images.pexels.com/photos/6863183/pexels-photo-6863183.jpeg?auto=compress&cs=tinysrgb&w=400',
    category: 'creative',
    features: ['Efeito rose gold', 'Campos para serviços de beleza', 'Área para feedback'],
    watermarkRemoval: true,
    customHeader: true,
    customFooter: true,
    attachmentSupport: true,
    priceId: 'price_1Rk95pBICvnzUZeSUfbjhprN'
  },
  {
    id: 'teal-modern',
    name: 'Teal Modern',
    description: 'Modern teal design perfect for tech companies',
    price: 1.00,
    isPremium: true,
    colors: {
      primary: '#0D9488',
      secondary: '#0F766E',
      accent: '#14B8A6',
      text: '#374151',
      background: '#F0FDFA'
    },
    logo: 'https://images.pexels.com/photos/3184292/pexels-photo-3184292.jpeg?auto=compress&cs=tinysrgb&w=100',
    preview: 'https://images.pexels.com/photos/6863183/pexels-photo-6863183.jpeg?auto=compress&cs=tinysrgb&w=400',
    category: 'modern',
    features: ['Layout moderno', 'Seção técnica', 'Especificações de produto'],
    watermarkRemoval: true,
    customHeader: true,
    customFooter: true,
    attachmentSupport: true,
    multiCurrency: true,
    priceId: 'price_1Rk95pBICvnzUZeSUfbjhprN'
  },
  {
    id: 'amber-warm',
    name: 'Amber Warm',
    description: 'Warm amber design for consulting and services',
    price: 1.00,
    isPremium: true,
    colors: {
      primary: '#D97706',
      secondary: '#B45309',
      accent: '#F59E0B',
      text: '#374151',
      background: '#FFFBEB'
    },
    logo: 'https://images.pexels.com/photos/3184340/pexels-photo-3184340.jpeg?auto=compress&cs=tinysrgb&w=100',
    preview: 'https://images.pexels.com/photos/6863183/pexels-photo-6863183.jpeg?auto=compress&cs=tinysrgb&w=400',
    category: 'business',
    features: ['Tom amigável', 'Seção de consultoria', 'Formato de proposta'],
    watermarkRemoval: true,
    customHeader: true,
    customFooter: true,
    attachmentSupport: true,
    multiCurrency: true,
    digitalSignature: true,
    priceId: 'price_1Rk95pBICvnzUZeSUfbjhprN'
  },
  {
    id: 'indigo-corporate',
    name: 'Indigo Corporate',
    description: 'Professional indigo design for corporate use',
    price: 1.00,
    isPremium: true,
    colors: {
      primary: '#4F46E5',
      secondary: '#4338CA',
      accent: '#6366F1',
      text: '#374151',
      background: '#F8FAFC'
    },
    logo: 'https://images.pexels.com/photos/3184293/pexels-photo-3184293.jpeg?auto=compress&cs=tinysrgb&w=100',
    preview: 'https://images.pexels.com/photos/6863183/pexels-photo-6863183.jpeg?auto=compress&cs=tinysrgb&w=400',
    category: 'corporate',
    features: ['Layout corporativo', 'Seções financeiras avançadas', 'Detalhes fiscais'],
    watermarkRemoval: true,
    customHeader: true,
    customFooter: true,
    attachmentSupport: true,
    multiCurrency: true,
    digitalSignature: true,
    itemImages: true,
    priceId: 'price_1Rk95pBICvnzUZeSUfbjhprN'
  },
  {
    id: 'slate-minimal',
    name: 'Slate Minimal',
    description: 'Clean minimal design with slate accents',
    price: 1.00,
    isPremium: true,
    colors: {
      primary: '#64748B',
      secondary: '#475569',
      accent: '#94A3B8',
      text: '#334155',
      background: '#F8FAFC'
    },
    logo: 'https://images.pexels.com/photos/3184294/pexels-photo-3184294.jpeg?auto=compress&cs=tinysrgb&w=100',
    preview: 'https://images.pexels.com/photos/6863183/pexels-photo-6863183.jpeg?auto=compress&cs=tinysrgb&w=400',
    category: 'minimal',
    features: ['Design minimalista', 'Tipografia elegante', 'Espaçamento perfeito'],
    watermarkRemoval: true,
    customHeader: true,
    customFooter: true,
    attachmentSupport: true,
    multiCurrency: true,
    digitalSignature: true,
    itemImages: true,
    priceId: 'price_1Rk95pBICvnzUZeSUfbjhprN'
  },
  {
    id: 'premium-logo',
    name: 'Premium + Logo',
    description: 'Comprehensive template with logo and all professional fields',
    price: 1.00,
    isPremium: true,
    colors: {
      primary: '#0891B2',
      secondary: '#0E7490',
      accent: '#22D3EE',
      text: '#374151',
      background: '#ECFEFF'
    },
    logo: 'https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=100',
    preview: 'https://images.pexels.com/photos/6863183/pexels-photo-6863183.jpeg?auto=compress&cs=tinysrgb&w=400',
    priceId: 'price_1Rk95pBICvnzUZeSUfbjhprN',
    category: 'business',
    features: ['Template completo', 'Suporte premium', 'Todas as funcionalidades', 'Atualizações gratuitas'],
    watermarkRemoval: true,
    customHeader: true,
    customFooter: true,
    attachmentSupport: true,
    multiCurrency: true,
    digitalSignature: true,
    itemImages: true
  }
];